import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { t } from 'i18next'

import { useOSDetection } from '@/hooks/detectOS/useOSDetection'

import { TPlan } from '@/config/types'
import { CredentialsTypes } from '@/enums/credentialsTypes'
import { MobileOperationSystem } from '@/enums/mobileOperationSystem'
import { PageName } from '@/enums/pageName'
import { CheckoutFlowVersion, PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { getPath } from '@/utils/urlUtils'

import { PrimaryButton } from '@/components/buttons/primaryButton'
import { SuccessPageProgressBar } from '@/components/successPageProgressBar'
import { TextHTML } from '@/components/typography'
import { GreenEllipse } from '@/shared/icons/GreenEllipse'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageView } from '@/events/events'

import { useLTPData, useUserCredentials } from './hooks'
import { TPaymentSuccessfulPageProps } from './types'

const REQUEST_TIMEOUT = 50000

const pageName = PageName.paymentSuccess

const NO_USER_REDIRECTION_MAP: Record<CheckoutFlowVersion, string> = {
  [CheckoutFlowVersion.V1]: getPath(PageName.registration),
  [CheckoutFlowVersion.V2]: getPath(PageName.verificationV2),
}

const redirectToApp = (link: string) => {
  window.open(link)
}

export const HIMPaymentSuccessfulPage = ({
  pageConfig,
  getItem,
  config,
}: TPaymentSuccessfulPageProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const navigation = useNavigate()
  const [isShowTooltip, setShowTooltip] = useState<'username' | 'password' | null>(null)

  const {
    loadingTitle,
    manualTitle,
    manualProvidedEmail,
    followPassword,
    pageTitle,
    manualHeader,
    manualSteps,
    manualStepsPartialCredentials,
    screenshots,
    plansDetails,
    isShowPageProgressBar = false,
    ltpStep,
  } = pageConfig

  const selectedPlanId = getItem(PageName.selectPlan)
  const planData = useMemo<TPlan | undefined>(
    () => plansDetails.find((plan) => plan.planId === selectedPlanId),
    [plansDetails, selectedPlanId]
  )
  const paymentMethod = (getItem('paymentMethod') as PaymentMethod) || undefined
  const paymentPlatform = (getItem('paymentPlatform') as PaymentPlatform) || undefined

  const mobileOS = useOSDetection()

  const copyToClipboard = (text: string, tooltipName: 'username' | 'password') => () => {
    navigator.clipboard.writeText(text)
    setShowTooltip(tooltipName)
  }

  useEffect(
    () => {
      trackPageView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user,
        [],
        [],
        planData?.internalPlanId,
        paymentMethod,
        paymentPlatform
      )

      document.title = pageTitle
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (!user) {
      const navigateTo = NO_USER_REDIRECTION_MAP[sessionContext.checkoutFlowVersion]

      navigation(navigateTo)
    }
  }, [user, navigation, sessionContext])

  const [userCredentials, , isLoading] = useUserCredentials({
    credentialsType: CredentialsTypes.PASSWORD,
    requestTimeout: REQUEST_TIMEOUT,
    retryTimeout: 10000,
    errorRetryTimeout: 5000,
    pageName,
    internalPlanId: planData?.internalPlanId,
    paymentMethod,
    paymentPlatform,
    config,
  })

  const [ltpData, ,] = useLTPData({
    requestTimeout: REQUEST_TIMEOUT,
    retryTimeout: 10000,
    errorRetryTimeout: 5000,
    pageName,
    config,
    getItem,
    disabled: !ltpStep,
  })

  const stepsForAuthorisation = useMemo(() => {
    if (!userCredentials || !('username' in userCredentials)) {
      return manualProvidedEmail
    }
    if (userCredentials.password) {
      return manualSteps
    } else {
      return manualStepsPartialCredentials
    }
  }, [userCredentials, manualSteps, manualStepsPartialCredentials, manualProvidedEmail])

  useEffect(() => {
    const id = setTimeout(() => {
      setShowTooltip(null)

      return () => {
        clearInterval(id)
      }
    }, 1500)
  }, [isShowTooltip])

  const className = `page page-${pageName}`

  const copyText = `${t('paymentSuccessPage.copyText') || 'Copy'}`
  const copiedText = `${t('paymentSuccessPage.copiedText') || 'Copied:'}`
  const buttonName = `${t('paymentSuccessPage.buttonName') || 'Download the app'}`

  let stepOffset = 0
  if (ltpStep && ltpData) {
    stepOffset++
  }

  return (
    <div className={className}>
      {isShowPageProgressBar && <SuccessPageProgressBar />}
      {manualTitle && <div className="success-title">{manualTitle}</div>}
      {isLoading ? (
        <TextHTML className="loading-title" title={loadingTitle} />
      ) : (
        <>
          <div className="manual-steps-title">{manualHeader}</div>
          <div className="steps-container">
            {ltpStep && stepsForAuthorisation && ltpData && (
              <div className="manual-step" key="ltp">
                <div className="step-icon">
                  <GreenEllipse />
                  <div className="step-number">{1}</div>
                </div>
                <TextHTML className="text" title={ltpStep.title + ltpData.name} />
              </div>
            )}
            {stepsForAuthorisation &&
              stepsForAuthorisation.map((step) => {
                let stepTitle = step.title

                if (mobileOS === MobileOperationSystem.ANDROID && step.androidTitle) {
                  stepTitle = step.androidTitle
                  stepTitle = stepTitle.replace('${APP_GOOGLE_LINK}', config.APP_GOOGLE_LINK)
                } else if (/*mobileOS === MobileOperationSystem.IOS &&*/ step.iosTitle) {
                  stepTitle = step.iosTitle
                  stepTitle = stepTitle.replace('${APP_APPLE_LINK}', config.APP_APPLE_LINK)
                }

                return (
                  <div className="manual-step" key={step.id}>
                    <div className="step-icon">
                      <GreenEllipse />
                      <div className="step-number">{step.id + stepOffset}</div>
                    </div>
                    <TextHTML className="text" title={stepTitle} />
                  </div>
                )
              })}

            {userCredentials && 'username' in userCredentials && (
              <div className="inputs-container">
                <div className="description-container">
                  <div className="input-like"> {userCredentials.username} </div>
                  <button
                    className="copy-button"
                    onClick={copyToClipboard(userCredentials.username, 'username')}
                  >
                    {copyText}
                  </button>
                  {isShowTooltip === 'username' && (
                    <span className="copy-tooltip">
                      {copiedText} <br />
                      {userCredentials.username}
                    </span>
                  )}
                </div>
                {userCredentials.password && (
                  <>
                    {followPassword && <TextHTML className="text" title={followPassword} />}
                    <div className="description-container">
                      <div className="input-like"> {userCredentials.password} </div>
                      <button
                        className="copy-button"
                        onClick={copyToClipboard(userCredentials.password, 'password')}
                      >
                        {copyText}
                      </button>
                      {isShowTooltip === 'password' && (
                        <span className="copy-tooltip">
                          {copiedText} <br />
                          {userCredentials.password}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {userCredentials && (
            <>
              <div className="button-container">
                <PrimaryButton
                  onClick={() =>
                    redirectToApp(
                      mobileOS === MobileOperationSystem.ANDROID
                        ? config.APP_GOOGLE_LINK
                        : config.APP_APPLE_LINK
                    )
                  }
                >
                  {buttonName}
                </PrimaryButton>
              </div>
            </>
          )}
          {screenshots && (
            <div className="manual-steps-screenshots">
              <img src={screenshots.first} alt="" />
            </div>
          )}
        </>
      )}
    </div>
  )
}
